import { useEffect, useState, type ReactNode } from "react";
import { AnnouncerContext, DialogRegionContext, type Announce, type RegisterDialogRegion } from "./announcer-context";

/** The text lands this long after `announce`, so a cleared region is seen empty first and a repeat is spoken again. */
const LAND_DELAY_MS = 50;
/** A region that vanishes later than this after its message landed takes the message with it (it was there long enough). */
const HAND_BACK_MS = 10_000;

/**
 * The region that should speak now: the one in the top-most open modal dialog, else none (the app's own). A later dialog is on
 * top unless it holds the one found so far (an outer dialog registered after an inner one); a region whose dialog is closed or
 * gone never speaks.
 */
function topRegion(regions: readonly HTMLElement[]): HTMLElement | null {
  let best: { region: HTMLElement; dialog: HTMLDialogElement } | null = null;
  for (const region of regions) {
    const dialog = region.closest("dialog");
    if (!dialog || !dialog.open || !region.isConnected) continue;
    if (best === null || !dialog.contains(best.dialog)) best = { region, dialog };
  }
  return best?.region ?? null;
}

/** Puts `text` in a dialog's region (the announcer owns that text; React renders the region empty). */
function speak(region: HTMLElement, text: string) {
  region.textContent = text;
}

/** The latest message once it has landed in a dialog's region. */
interface Landed {
  text: string;
  region: HTMLElement;
  at: number;
  /** Stops listening for that region's dialog closing. */
  release: () => void;
}

/**
 * The announcer's state, outside React (one per provider). `setPageMessage` writes the page's region.
 *
 * Announce, then close: most dialogs announce their result, await a refetch and only then close. When the refetch outlasts the
 * landing delay the text lands in the dialog's own region, and that region leaves with the dialog, so the message was never
 * heard. So the region holding the LATEST message hands it on when it goes (unmounted, or its dialog closed): to the top-most
 * dialog still open, else to the page's region. Within `HAND_BACK_MS` of landing only; a newer `announce` cancels it.
 */
function createAnnouncer(setPageMessage: (text: string) => void) {
  let timer: number | undefined;
  let regions: HTMLElement[] = [];
  let landed: Landed | null = null;

  function forget() {
    landed?.release();
    landed = null;
  }

  function land(text: string) {
    forget();
    const region = topRegion(regions);
    if (!region) {
      setPageMessage(text);
      return;
    }
    speak(region, text);
    const dialog = region.closest("dialog");
    const onClose = () => handBack(region);
    dialog?.addEventListener("close", onClose);
    landed = { text, region, at: Date.now(), release: () => dialog?.removeEventListener("close", onClose) };
  }

  function schedule(text: string) {
    window.clearTimeout(timer);
    timer = window.setTimeout(() => land(text), LAND_DELAY_MS);
  }

  /** `region` is going (removed, or its dialog closed): if it holds the latest message, speak it again where it can be heard. */
  function handBack(region: HTMLElement) {
    const last = landed;
    if (!last || last.region !== region) return;
    forget();
    speak(region, "");
    if (Date.now() - last.at > HAND_BACK_MS) return;
    // Chosen when it lands, among the regions left (the going one is out of the list by then, its dialog out of the top layer).
    schedule(last.text);
  }

  const announce: Announce = (next) => {
    // Clear first so repeating the same message is announced again.
    forget();
    setPageMessage("");
    for (const region of regions) speak(region, "");
    // Where to speak is decided when the text lands: a dialog that closes right after announcing (Save, then close) hands the
    // message back to the app's region.
    schedule(next);
  };

  const register: RegisterDialogRegion = (region) => {
    regions = [...regions, region];
    return () => {
      regions = regions.filter((each) => each !== region);
      handBack(region);
    };
  };

  function dispose() {
    window.clearTimeout(timer);
    forget();
  }

  return { announce, register, dispose };
}

/**
 * Renders the app's ONE polite live region. Never use it for ticking timers. Batch 26 (ruling of 20 Oct): while a modal dialog is
 * open, the same `announce` speaks through that dialog's own region (`DialogLiveRegion`), because the browser hides this one
 * behind the modal. Exactly one region holds the message at a time, so nothing is spoken twice; a message whose dialog closes
 * soon after it landed moves on to where it can still be heard (`createAnnouncer`).
 */
export function AnnouncerProvider({ children }: { children: ReactNode }) {
  const [message, setMessage] = useState("");
  const [announcer] = useState(() => createAnnouncer(setMessage));

  useEffect(() => () => announcer.dispose(), [announcer]);

  return (
    <AnnouncerContext value={announcer.announce}>
      <DialogRegionContext value={announcer.register}>
        {children}
        <div role="status" aria-live="polite" className="vh" data-testid="announcer">
          {message}
        </div>
      </DialogRegionContext>
    </AnnouncerContext>
  );
}
